import React, {FC, useCallback, useEffect} from 'react'
import {Trans, useTranslation} from 'react-i18next'

import ExpandMoreIcon from '@mui/icons-material/ExpandMoreSharp'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Fade,
  Typography,
  useTheme,
} from '@mui/material'
import {getProductLineColors, PGTheme} from '@progynyglobal/pg-ui-library'
import parse from 'html-react-parser'
import {makeStyles} from 'tss-react/mui'

import {BackButton} from '@progyny/components/Buttons'
import PageLoader from '@progyny/components/PageLoader'
import {useFaqSectionsQuery} from '@progyny/generated/graphql'
import {useProductLine} from '@progyny/hooks/useProductLine'
import QuestionBanner from '@progyny/pages/KnowledgeCenter/Article/Details/TopicDetails/QuestionBanner'
import {useRefetchContext} from '@progyny/store/refetchStore'

const useStyles = makeStyles<{colors?: any}>()((theme, {colors}) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    '&::before': {
      content: "''",
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: 456,
      backgroundColor: colors.primary,
      zIndex: -1,
      [theme.breakpoints.down('sm')]: {
        height: 340,
      },
    },
  },
  faqSection: {
    backgroundColor: PGTheme.common.white,
    padding: 32,
    borderRadius: 8,
    maxWidth: 840,
    marginBottom: 80,
    border: '1px solid #E0E0E0',
    [theme.breakpoints.down('sm')]: {
      padding: 16,
      borderRadius: 10,
      width: '100%',
      marginBottom: 32,
    },
  },
  accordionRoot: {
    border: '1px solid #E0E0E0',
    borderRadius: 8,
    marginBottom: 16,
    boxShadow: 'none',
    '&:before': {
      display: 'none', // removes MUI default divider line
    },
  },
  accordionSummary: {
    padding: 16,
    margin: 0,
    minHeight: 'unset',
    '& .MuiAccordionSummary-content': {
      margin: 0,
    },
  },
  accordionDetails: {
    padding: 16,
    paddingTop: 0,
    marginTop: -16,
  },
  richText: {
    '& a:link, & a:visited': {
      color: colors.primary,
      textDecoration: 'underline',
    },
    '& a:hover': {
      color: PGTheme.primary.dark,
    },
  },
  answer: {
    whiteSpace: 'pre-wrap',
  },
}))

type QuestionProps = {
  questions: any
}

type Item = {
  answer: string
  question: string
}

const FaqQuestions: FC<QuestionProps> = (props) => {
  const {questions} = props
  const {productLine} = useProductLine()
  const colors = getProductLineColors(productLine)
  const {classes} = useStyles({colors})
  const {t} = useTranslation()

  return (
    <>
      {questions.map((item: Item, index: number) => (
        <Fade in={true} key={index}>
          <Accordion
            square
            key={item.question}
            id={item.question}
            className={classes.accordionRoot}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              className={classes.accordionSummary}
            >
              <Typography variant={'subtitle1'} fontWeight={600}>
                {item.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              <Typography variant={'body2'} className={classes.richText}>
                {parse(t(item.answer))}
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Fade>
      ))}
    </>
  )
}

const Faq: FC = () => {
  const {t} = useTranslation()
  const previewToken = new URLSearchParams(window.location.search)
  const token = previewToken.get('preview')
  const {productLine} = useProductLine()
  const colors = getProductLineColors(productLine)
  const {registerRefetch} = useRefetchContext()
  const {
    data: faqSections,
    loading: faqLoading,
    refetch,
  } = useFaqSectionsQuery({
    variables: {preview: token},
  })

  const theme = useTheme()

  const {classes} = useStyles({colors})
  const stableRefetch = useCallback(() => {
    refetch()
  }, [refetch])

  useEffect(() => {
    registerRefetch('faqSections', stableRefetch)
  }, [registerRefetch, stableRefetch])

  useEffect(() => {
    void refetch({preview: token})
  }, [productLine, refetch, token])

  return (
    <Box
      mt={{xs: 10, md: 13}}
      mb={{xs: 12, md: 20}}
      paddingX={{xs: 2, md: 0}}
      className={classes.container}
    >
      <Box
        mb={{xs: 2, md: 6}}
        display='flex'
        justifyContent='flex-start'
        width='100%'
      >
        <BackButton color={theme.palette.other.brandColors.white} />
      </Box>
      <Typography
        variant={'h3'}
        textAlign={'center'}
        color={theme.palette.other.brandColors.white}
        gutterBottom
      >
        {t('profile_faq')}
      </Typography>
      <Box mt={1} width={{xs: '100%', md: '45%'}}>
        <Typography
          textAlign={'center'}
          variant={'body1'}
          color={theme.palette.other.brandColors.white}
        >
          <Trans i18nKey='faq_here_you_can_find_helpful' />
        </Typography>
      </Box>
      {faqLoading ? (
        <PageLoader position={'center'} />
      ) : (
        <>
          <Box mt={{xs: 4, md: 15}} className={classes.faqSection}>
            {faqSections?.faqSections.map((section, index) => (
              <Box key={index}>
                <Typography
                  variant={'h6'}
                  fontWeight={700}
                  sx={{mt: index === 0 ? 0 : 6, mb: index === 0 ? 4 : 3}}
                  gutterBottom
                >
                  {t(section?.title!)}
                </Typography>
                <FaqQuestions questions={section?.entries} />
              </Box>
            ))}
          </Box>
          <Box width={{xs: '100%', md: 'auto'}}>
            <QuestionBanner width={840} />
          </Box>
        </>
      )}
    </Box>
  )
}

export default Faq
